import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useCountry } from '@/contexts/CountryContext';
import { isContractorUser, isClientUser } from '@/types/user';
import DashboardContainer from '@/components/layout/DashboardContainer';
import { ExpenseProvider } from '@/contexts/ExpenseContext';
import ContractorExpenseList from '@/components/contractor/ContractorExpenseList';
import CreateExpenseForm from '@/components/contractor/CreateExpenseForm';
import ExpenseDetailsModal from '@/components/contractor/ExpenseDetailsModal';
import ExpenseSummaryDashboard from '@/components/contractor/ExpenseSummaryDashboard';
import ClientExpenseDashboard from '@/components/client/ClientExpenseDashboard';
import { Expense, ExpenseContract, ExpenseStatus } from '@/types/expense';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';

/**
 * Main expense management page that adapts based on user role
 * - Contractors: Full expense management (create, view, edit, submit)
 * - Clients: Expense approval and invoicing workflows
 */
const ExpenseManagementPage: React.FC = () => {
  const { user } = useAuth();
  const { currentCountry, getCurrencySymbol, formatCurrency } = useCountry();
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [contracts, setContracts] = useState<ExpenseContract[]>([]);
  const [clientContracts, setClientContracts] = useState<any[]>([]);
  const [expenseFilters, setExpenseFilters] = useState<ExpenseStatus[]>([]);
  const expenseListRef = useRef<HTMLDivElement>(null);

  const handleCreateExpense = () => {
    setIsCreateModalOpen(true);
  };

  const handleViewExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    setIsDetailsModalOpen(true);
  };

  const handleEditExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    setIsCreateModalOpen(true);
  };

  const handleCloseModals = () => {
    setIsCreateModalOpen(false);
    setIsDetailsModalOpen(false);
    setSelectedExpense(null);
  };

  const handleViewDrafts = () => {
    setExpenseFilters([ExpenseStatus.DRAFT]);
    setTimeout(() => {
      expenseListRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }, 100);
  };

  const handleViewRejected = () => {
    setExpenseFilters([ExpenseStatus.REJECTED]);
    setTimeout(() => {
      expenseListRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }, 100);
  };

  const handleClearFilters = () => {
    setExpenseFilters([]);
  };

  useEffect(() => {
    // Log country context for debugging
    console.log('[ExpenseManagementPage] Current country:', currentCountry?.code, currentCountry?.currencySymbol);
    
    const fetchContracts = async () => {
      try {
        if (isContractorUser(user)) {
          const response = await ApiService.request<{ 
            contracts: Array<{
              id: string;
              contractTitle: string;
              status: string;
              clientInvitationEmail?: string;
            }>
          }>(
            EndpointKeys.GET_CONTRACTOR_CONTRACTS
          );
          
          // Map backend contract format to ExpenseContract format
          const mappedContracts: ExpenseContract[] = (response.contracts || []).map(contract => ({
            id: contract.id,
            title: contract.contractTitle || 'Untitled Contract',
            clientName: contract.clientInvitationEmail || 'No client assigned',
            status: contract.status
          }));
          
          setContracts(mappedContracts);
        } else if (isClientUser(user)) {
          const response = await ApiService.request<{
            contracts: Array<{
              id: string;
              contractTitle: string;
              status: string;
              contractorName?: string;
            }>
          }>(
            EndpointKeys.GET_CLIENT_CONTRACTS
          );
          
          setClientContracts(response.contracts || []);
        }
      } catch (error) {
        console.error('Error fetching contracts:', error);
        setContracts([]);
        setClientContracts([]);
      }
    };

    fetchContracts();
  }, [user]);

  // Client users see the expense approval dashboard
  if (isClientUser(user)) {
    return (
      <DashboardContainer title="Expense Management">
        <ExpenseProvider>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Review and approve contractor expenses for invoicing
              {currentCountry && (
                <span className="ml-2 text-sm">
                  (Amounts shown in {currentCountry.currencyCode} {currentCountry.currencySymbol})
                </span>
              )}
            </p>
            <ClientExpenseDashboard 
              contractId={clientContracts.length > 0 ? clientContracts[0].id : ""}
              contractorName={clientContracts.length > 0 ? clientContracts[0].contractorName : undefined}
            />
          </div>
        </ExpenseProvider>
      </DashboardContainer>
    );
  }

  // Contractor users see the full expense management interface
  if (isContractorUser(user)) {
    return (
      <DashboardContainer title="Expense Management">
        <ExpenseProvider>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Create, submit, and manage your business expenses
              {currentCountry && (
                <span className="ml-2 text-sm">
                  (Amounts shown in {currentCountry.currencyCode} {currentCountry.currencySymbol})
                </span>
              )}
            </p>
            
            <div className="space-y-8">
              {/* Summary Dashboard */}
              <ExpenseSummaryDashboard
                onCreateExpense={handleCreateExpense}
                onViewDrafts={handleViewDrafts}
                onViewRejected={handleViewRejected}
              />

              {/* Expense List */}
              <ContractorExpenseList
                onCreateExpense={handleCreateExpense}
                onViewExpense={handleViewExpense}
                onEditExpense={handleEditExpense}
                statusFilters={expenseFilters}
                onClearFilters={handleClearFilters}
                ref={expenseListRef}
              />

              {/* Modals */}
              <Dialog open={isCreateModalOpen} onOpenChange={handleCloseModals}>
                <DialogContent className="sm:max-w-[800px] max-w-[95vw] max-h-[95vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Expense</DialogTitle>
                  </DialogHeader>
                  <CreateExpenseForm
                    onSuccess={() => {
                      handleCloseModals();
                      // Optionally refresh data
                    }}
                    onCancel={handleCloseModals}
                    availableContracts={contracts}
                    className="border-0 shadow-none"
                  />
                </DialogContent>
              </Dialog>

              <ExpenseDetailsModal
                expense={selectedExpense}
                isOpen={isDetailsModalOpen}
                onClose={handleCloseModals}
              />
            </div>
          </div>
        </ExpenseProvider>
      </DashboardContainer>
    );
  }

  // Fallback for other user types
  return (
    <DashboardContainer title="Access Denied">
      <div className="text-center py-8">
        <p className="text-muted-foreground">
          Expense management is only available for contractors and clients.
        </p>
      </div>
    </DashboardContainer>
  );
};

export default ExpenseManagementPage; 